<template>
  <modal @close="closeModal">
    <div class="upload-modal">
      <div class="title">Please upload your Batch file</div>
      <input type="file" @change="selectFile" />
      <div v-if="isUploading" class="feedback">
        Uploading {{ fileData.name }}, please wait&hellip;
      </div>
      <div class="action-controls">
        <button class="white" @click="closeModal" :disabled="isUiLocked">Cancel</button>
        <button @click="uploadFile" :disabled="isUiLocked || !fileData">Upload</button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import Modal from '@/components/Modal.vue';
import { useBatch, useToast } from '@/hooks';
import { throttle } from 'lodash';

export default defineComponent({
  name: 'ModalUploadFile',
  components: {
    Modal,
  },
  emits: ['close', 'complete'],
  setup(props, { emit }) {
    const { isUploading, uploadResponse, uploadSuccess, uploadCSV } = useBatch();
    const { toggleToast } = useToast();
    const fileData = ref<File | null>();

    const selectFile = (e: { target: HTMLInputElement }) => {
      fileData.value = e.target.files?.length ? e.target.files[0] : null;
    };

    /**
     * Lock the UI while the API is submitting,
     * and keep it locked once the API returns successfully, until the Modal closes itself
     */
    const isUiLocked = computed(() => {
      return isUploading.value || uploadSuccess.value === true;
    });

    const closeModal = () => {
      emit('close');
    };

    const _uploadFile = throttle(() => {
      if (!fileData.value) return;

      const formData = new FormData();
      formData.append('file', fileData.value, fileData.value.name);

      uploadCSV(formData);
    }, 100);

    const uploadFile = () => {
      _uploadFile();
    };

    watch(uploadSuccess, () => {
      if (uploadSuccess.value) {
        toggleToast(true, 'success', `Successfully uploaded file ${fileData.value?.name}`);
        closeModal();
        emit('complete');
      }
    });

    return {
      fileData,
      selectFile,
      closeModal,
      uploadFile,
      isUiLocked,
      isUploading,
      uploadResponse,
    };
  },
});
</script>

<style lang="scss">
.upload-modal {
  min-width: 30rem;
}
</style>
